import {
	ButtonsContainer,
	CustomFieldsForm,
	FormContainer,
	FormSeparator,
	StyledButton,
	DeleteButton,
	CustomFieldsFooter,
	RowFlex,
} from './CustomFields.styles';
import { CustomFieldFormProps, CustomFieldsProps } from './types';
import { defaultValues, schema } from './utils';

import { CustomFieldsHeader } from './components/CustomFieldsHeader';
import { Icon } from 'features/common/components/Icon';
import { MainLayout } from 'layouts/MainLayout';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupPageShell } from 'features/common/components/Punchup/pages/PunchupPageShell';
import { QuestionForm } from './components/QuestionForm';
import { RightForm } from './components/RightForm/RightForm';
import { RootState } from 'store';
import { useCustomFieldsApi } from './useCustomFieldsApi';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';

export const CustomFields = ({ editMode = false }: CustomFieldsProps) => {
	const { t } = useTranslation();
	const { goBack } = useHistory();
	const { isPunchup } = useSelector((state: RootState) => state.auth);

	// In Punchup create mode, start with no pre-selected radio values so the
	// user makes each choice deliberately rather than seeing defaults filled in.
	const punchupCreateDefaults =
		isPunchup && !editMode
			? { ...defaultValues, custom_field: { ...defaultValues.custom_field, only_buyer: '', is_required: '' } }
			: defaultValues;

	const formMethods = useForm<CustomFieldFormProps>({
		defaultValues: punchupCreateDefaults,
		resolver: yupResolver(schema),
	});

	const { event, createCustomField, updateCustomField, deleteCustomField } = useCustomFieldsApi(formMethods.reset);

	useEffect(() => {
		if (event) {
			formMethods.setValue('ticket_types_ids', event.ticket_types.map((ticket) => ticket.ID) ?? []);
		}
	}, [event]);

	const handleSubmit = (values: CustomFieldFormProps) => {
		if (editMode) updateCustomField(values);
		else createCustomField(values);
	};

	if (!event) return null;

	const handleDelete = () => {
		deleteCustomField();
	};

	const form = (
		<CustomFieldsForm formMethods={formMethods} onSubmit={handleSubmit} isPunchup={isPunchup}>
			<FormContainer isPunchup={isPunchup}>
				<QuestionForm />
				<FormSeparator isPunchup={isPunchup} />
				<RightForm event={event} />
			</FormContainer>

			<ButtonsContainer isPunchup={isPunchup}>
				{!isPunchup ? (
					<CustomFieldsFooter>
						<StyledButton text={t('button-cancel')} type="button" onClick={goBack} category="outlined" />
						<StyledButton
							text={t(editMode ? 'save' : 'add')}
							type="submit"
							disabled={formMethods.watch('ticket_types_ids').length == 0}
						/>
					</CustomFieldsFooter>
				) : (
					<CustomFieldsFooter>
						{editMode && (
							<DeleteButton type="button" onClick={handleDelete}>
								<Icon iconName="trash" size="M" />
								{t('button-delete')}
							</DeleteButton>
						)}
						<RowFlex>
							<PunchupButton text={t('button-cancel')} type="button" onClick={goBack} variant="secondary" />
							<PunchupButton
								text={t(editMode ? 'save' : 'add')}
								type="submit"
								disabled={formMethods.watch('ticket_types_ids').length == 0}
							/>
						</RowFlex>
					</CustomFieldsFooter>
				)}
			</ButtonsContainer>
		</CustomFieldsForm>
	);

	if (isPunchup) {
		return (
			<PunchupPageShell pageName={editMode ? 'Edit Custom Field' : 'Custom Field'} onBack={goBack}>
				{form}
			</PunchupPageShell>
		);
	}

	return (
		<MainLayout>
			<CustomFieldsHeader title={event.name ?? ''} editMode={!!editMode} onDelete={deleteCustomField} />
			{form}
		</MainLayout>
	);
};
